import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { KanbanCard, WorkflowStage } from '@tailoring-crm/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@tailoring-crm/ui';

/**
 * Sending a garment back always asks why.
 *
 * Not as a formality: a reversal is the change people argue about weeks later — the karigar who
 * was not paid for work that was undone, the customer told their lehenga was ready twice. The
 * reason is stored on the stage event and in the audit trail, so "why is this back in cutting?"
 * has an answer that is not somebody's memory. The confirm button stays disabled until there is
 * one, which is the same rule the API enforces.
 *
 * The rework switch distinguishes the two things a shop means: the work FAILED (rework — the
 * garment genuinely goes back through a stage), or the board was wrong (a correction).
 */
export interface ReasonDialogProps {
  card: KanbanCard;
  toStage: WorkflowStage;
  stageLabel: string;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: (input: { reason: string; rework: boolean }) => void;
}

export function ReasonDialog({
  card,
  toStage: _toStage,
  stageLabel,
  pending = false,
  onCancel,
  onConfirm,
}: ReasonDialogProps): ReactElement {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [rework, setRework] = useState(true);

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('kanban.sendBackTitle', { garment: card.garmentTypeLabel, stage: stageLabel })}
          </DialogTitle>
          <DialogDescription>{t('kanban.sendBackHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stage-reason">{t('kanban.reasonLabel')}</Label>
            <Input
              id="stage-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('kanban.reasonPlaceholder')}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={rework}
              onChange={(event) => setRework(event.target.checked)}
              className="size-4 accent-primary"
            />
            {t('kanban.isRework')}
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3 || pending}
            onClick={() => onConfirm({ reason: reason.trim(), rework })}
          >
            {t('kanban.sendBackConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
